import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, Loader2, AlertTriangle, TrendingUp, DollarSign,
  Truck, Package, ChevronDown, ChevronRight, Award,
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR } from '@/utils/formatters';

interface LinkedPO {
  poId: string;
  poNumber: string;
  vendorName: string;
  rate: number;
  qty: number;
}

interface MarginItem {
  id: string;
  description: string;
  qty: number;
  sellingRate: number;
  sellingTotal: number;
  purchaseCost: number;
  freightShare: number;
  grossProfit: number;
  marginPct: number;
  linkedPOs: LinkedPO[];
}

interface VendorQuote {
  vendorName: string;
  quotedRate: number;
  deliveryDays: number;
  marginPct: number;
  awarded: boolean;
}

interface QuotationJob {
  id: string;
  itemDescription: string;
  vendors: VendorQuote[];
}

interface MarginData {
  soNumber: string;
  soId: string;
  totalValue: number;
  currency: string;
  totalSellingPrice: number;
  totalPurchaseCost: number;
  totalFreight: number;
  totalGrossProfit: number;
  overallMarginPct: number;
  items: MarginItem[];
  quotationJobs: QuotationJob[];
}

function marginColor(pct: number): string {
  if (pct >= 20) return 'text-green-600';
  if (pct >= 10) return 'text-yellow-600';
  return 'text-red-600';
}

function marginBg(pct: number): string {
  if (pct >= 20) return 'bg-green-50 text-green-700';
  if (pct >= 10) return 'bg-yellow-50 text-yellow-700';
  return 'bg-red-50 text-red-700';
}

export function MarginCalculatorPage() {
  const navigate = useNavigate();
  const { soId } = useParams<{ soId: string }>();
  const [data, setData] = useState<MarginData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!soId) return;
    setLoading(true);
    api.get(`/margin-calculator/${soId}/margins`)
      .then(res => {
        const raw = res.data.data;
        // Map API response shape to frontend shape
        const mapped: MarginData = {
          soNumber: raw.salesOrder?.number ?? '',
          soId: raw.salesOrder?.id ?? soId,
          totalValue: raw.salesOrder?.total ?? 0,
          currency: raw.salesOrder?.currency ?? 'INR',
          totalSellingPrice: raw.totals?.totalSellingPrice ?? 0,
          totalPurchaseCost: raw.totals?.totalPurchaseCost ?? 0,
          totalFreight: raw.totals?.totalFreightCost ?? 0,
          totalGrossProfit: raw.totals?.totalGrossProfit ?? 0,
          overallMarginPct: raw.totals?.overallMarginPct ?? 0,
          items: (raw.items ?? []).map((item: any) => ({
            id: item.id,
            description: item.description,
            qty: item.quantity ?? 0,
            sellingRate: item.rate ?? 0,
            sellingTotal: item.sellingPrice ?? 0,
            purchaseCost: item.purchaseCost ?? 0,
            freightShare: item.freightCostShare ?? 0,
            grossProfit: item.grossProfit ?? 0,
            marginPct: item.grossMarginPct ?? 0,
            linkedPOs: (item.purchaseOrders ?? []).map((po: any) => ({
              poId: po.poId ?? '',
              poNumber: po.poNumber ?? '',
              vendorName: po.vendorName ?? '',
              rate: po.rate ?? 0,
              qty: po.quantity ?? 0,
            })),
          })),
          quotationJobs: (raw.vendorComparison ?? []).map((vc: any) => ({
            id: vc.jobNumber ?? '',
            itemDescription: vc.description ?? '',
            vendors: (vc.quotations ?? []).map((q: any) => ({
              vendorName: q.vendorName ?? '',
              quotedRate: q.rate ?? 0,
              deliveryDays: q.deliveryDays ?? 0,
              marginPct: q.marginPct ?? 0,
              awarded: q.isAwarded ?? false,
            })),
          })),
        };
        setData(mapped);
      })
      .catch(() => setError('Failed to load margin data'))
      .finally(() => setLoading(false));
  }, [soId]);

  const toggleExpand = (id: string) => {
    setExpandedItems(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm" style={{ color: 'var(--text-muted)' }}>Loading margin data...</span>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center py-32" style={{ color: 'var(--text-muted)' }}>
        <AlertTriangle size={40} className="mb-2" />
        <p className="text-sm">{error || 'Margin data not found'}</p>
        <button onClick={() => navigate(-1)} className="mt-2 text-sm text-[var(--ink-0)] hover:underline">Go back</button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-6xl">
      {/* Header */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/sales-orders/${soId}`)}
            className="rounded-lg p-1.5 transition-colors hover:bg-gray-100"
          >
            <ArrowLeft size={20} style={{ color: 'var(--text-muted)' }} />
          </button>
          <div>
            <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>
              Margin Analysis — {data.soNumber}
            </h1>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              Total Order Value: {formatINR(data.totalValue)} {data.currency !== 'INR' ? `(${data.currency})` : ''}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/sales-orders/${soId}`)}
          className="flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-colors hover:bg-gray-50"
          style={{ borderColor: 'var(--border-primary)', color: 'var(--text-primary)' }}
        >
          <ArrowLeft size={14} />
          Back to SO
        </button>
      </div>

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
          <div className="mb-2 flex items-center gap-2">
            <DollarSign size={14} style={{ color: 'var(--text-muted)' }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Selling Price</span>
          </div>
          <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{formatINR(data.totalSellingPrice)}</p>
        </div>

        <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
          <div className="mb-2 flex items-center gap-2">
            <Package size={14} style={{ color: 'var(--text-muted)' }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Purchase Cost</span>
          </div>
          <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{formatINR(data.totalPurchaseCost)}</p>
        </div>

        <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
          <div className="mb-2 flex items-center gap-2">
            <Truck size={14} style={{ color: 'var(--text-muted)' }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Freight</span>
          </div>
          <p className="text-xl font-bold" style={{ color: 'var(--text-primary)' }}>{formatINR(data.totalFreight)}</p>
        </div>

        <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
          <div className="mb-2 flex items-center gap-2">
            <TrendingUp size={14} style={{ color: 'var(--text-muted)' }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Gross Profit</span>
          </div>
          <p className={`text-xl font-bold ${marginColor(data.overallMarginPct)}`}>{formatINR(data.totalGrossProfit)}</p>
        </div>

        <div className="rounded-xl border p-5" style={{ backgroundColor: 'var(--bg-surface)', borderColor: 'var(--border-primary)' }}>
          <div className="mb-2 flex items-center gap-2">
            <TrendingUp size={14} style={{ color: 'var(--text-muted)' }} />
            <span className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Overall Margin</span>
          </div>
          <p className={`text-2xl font-bold ${marginColor(data.overallMarginPct)}`}>
            {(data.overallMarginPct ?? 0).toFixed(1)}%
          </p>
        </div>
      </div>

      {/* Per-Item Margin Table */}
      <div className="mb-6 rounded-xl border overflow-hidden" style={{ borderColor: 'var(--border-primary)' }}>
        <div className="border-b px-5 py-3" style={{ backgroundColor: 'var(--bg-inset)', borderColor: 'var(--border-primary)' }}>
          <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
            Per-Item Margins ({data.items.length})
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr
                className="border-b text-left text-xs font-medium uppercase tracking-wider"
                style={{
                  backgroundColor: 'var(--bg-inset)',
                  borderColor: 'var(--border-primary)',
                  color: 'var(--text-muted)',
                }}
              >
                <th className="px-4 py-2 w-6"></th>
                <th className="px-4 py-2">Description</th>
                <th className="px-4 py-2 text-right">Qty</th>
                <th className="px-4 py-2 text-right">Selling Rate</th>
                <th className="px-4 py-2 text-right">Selling Total</th>
                <th className="px-4 py-2 text-right">Purchase Cost</th>
                <th className="px-4 py-2 text-right">Freight</th>
                <th className="px-4 py-2 text-right">Gross Profit</th>
                <th className="px-4 py-2 text-right">Margin %</th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
              {data.items.map(item => (
                <>
                  <tr
                    key={item.id}
                    className="cursor-pointer transition-colors"
                    style={{ backgroundColor: 'var(--bg-surface)' }}
                    onClick={() => item.linkedPOs.length > 0 && toggleExpand(item.id)}
                    onMouseEnter={e => (e.currentTarget.style.backgroundColor = 'var(--bg-inset)')}
                    onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'var(--bg-surface)')}
                  >
                    <td className="px-4 py-3">
                      {item.linkedPOs.length > 0 && (
                        expandedItems.has(item.id)
                          ? <ChevronDown size={14} style={{ color: 'var(--text-muted)' }} />
                          : <ChevronRight size={14} style={{ color: 'var(--text-muted)' }} />
                      )}
                    </td>
                    <td className="px-4 py-3 font-medium" style={{ color: 'var(--text-primary)' }}>
                      {item.description}
                    </td>
                    <td className="px-4 py-3 text-right" style={{ color: 'var(--text-primary)' }}>{item.qty}</td>
                    <td className="px-4 py-3 text-right" style={{ color: 'var(--text-muted)' }}>{formatINR(item.sellingRate)}</td>
                    <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-primary)' }}>{formatINR(item.sellingTotal)}</td>
                    <td className="px-4 py-3 text-right" style={{ color: 'var(--text-muted)' }}>{formatINR(item.purchaseCost)}</td>
                    <td className="px-4 py-3 text-right" style={{ color: 'var(--text-muted)' }}>{formatINR(item.freightShare)}</td>
                    <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-primary)' }}>{formatINR(item.grossProfit)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${marginBg(item.marginPct)}`}>
                        {(item.marginPct ?? 0).toFixed(1)}%
                      </span>
                    </td>
                  </tr>
                  {/* Linked POs sub-rows */}
                  {expandedItems.has(item.id) && item.linkedPOs.map(po => (
                    <tr key={`${item.id}-${po.poId}`} style={{ backgroundColor: 'var(--bg-inset)' }}>
                      <td className="px-4 py-2"></td>
                      <td className="px-4 py-2 pl-8 text-xs" style={{ color: 'var(--text-muted)' }}>
                        <button
                          onClick={e => { e.stopPropagation(); navigate(`/purchase-orders/${po.poId}`); }}
                          className="text-[var(--ink-0)] hover:underline font-medium"
                        >
                          {po.poNumber}
                        </button>
                        <span className="ml-2">— {po.vendorName}</span>
                      </td>
                      <td className="px-4 py-2 text-right text-xs" style={{ color: 'var(--text-muted)' }}>{po.qty}</td>
                      <td colSpan={2} className="px-4 py-2 text-right text-xs" style={{ color: 'var(--text-muted)' }}>
                        @ {formatINR(po.rate)}
                      </td>
                      <td colSpan={4}></td>
                    </tr>
                  ))}
                </>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Vendor Comparison Section */}
      {data.quotationJobs.length > 0 && (
        <div className="space-y-6">
          <h2 className="text-lg font-bold" style={{ color: 'var(--text-primary)' }}>Vendor Comparison</h2>
          {data.quotationJobs.map(job => {
            const maxRate = Math.max(...job.vendors.map(v => v.quotedRate), 1);
            return (
              <div
                key={job.id}
                className="rounded-xl border overflow-hidden"
                style={{ borderColor: 'var(--border-primary)' }}
              >
                <div className="border-b px-5 py-3" style={{ backgroundColor: 'var(--bg-inset)', borderColor: 'var(--border-primary)' }}>
                  <h3 className="text-sm font-semibold" style={{ color: 'var(--text-primary)' }}>
                    {job.itemDescription}
                  </h3>
                </div>

                {/* Comparison Table */}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr
                        className="border-b text-left text-xs font-medium uppercase tracking-wider"
                        style={{
                          backgroundColor: 'var(--bg-inset)',
                          borderColor: 'var(--border-primary)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        <th className="px-4 py-2">Vendor</th>
                        <th className="px-4 py-2 text-right">Quoted Rate</th>
                        <th className="px-4 py-2 text-right">Delivery Days</th>
                        <th className="px-4 py-2 text-right">Margin %</th>
                        <th className="px-4 py-2 text-center">Awarded</th>
                        <th className="px-4 py-2">Rate Comparison</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y" style={{ borderColor: 'var(--border-primary)' }}>
                      {job.vendors.map((vendor, idx) => (
                        <tr
                          key={idx}
                          style={{
                            backgroundColor: vendor.awarded ? 'var(--bg-inset)' : 'var(--bg-surface)',
                          }}
                          className={vendor.awarded ? 'ring-1 ring-inset ring-green-300' : ''}
                        >
                          <td className="px-4 py-3 font-medium" style={{ color: 'var(--text-primary)' }}>
                            <div className="flex items-center gap-2">
                              {vendor.awarded && <Award size={14} className="text-green-600" />}
                              {vendor.vendorName}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-right font-medium" style={{ color: 'var(--text-primary)' }}>
                            {formatINR(vendor.quotedRate)}
                          </td>
                          <td className="px-4 py-3 text-right" style={{ color: 'var(--text-muted)' }}>
                            {vendor.deliveryDays} days
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${marginBg(vendor.marginPct)}`}>
                              {(vendor.marginPct ?? 0).toFixed(1)}%
                            </span>
                          </td>
                          <td className="px-4 py-3 text-center">
                            {vendor.awarded ? (
                              <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
                                ✓
                              </span>
                            ) : (
                              <span style={{ color: 'var(--text-muted)' }}>✗</span>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div
                                className={`h-4 rounded ${vendor.awarded ? 'bg-green-500' : 'bg-gray-300'}`}
                                style={{ width: `${Math.max((vendor.quotedRate / maxRate) * 100, 8)}%`, maxWidth: '160px' }}
                              />
                              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                                {formatINR(vendor.quotedRate)}
                              </span>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
